import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { FebStatut, IApplication, IFicheExpressionBesoinResponse } from 'src/app/shared/models/fiche-expression-besoin';
import { DevisStatut } from 'src/app/shared/models/devis';
import { FicheExpressionBesoinService } from './fiche-expression-besoin.service';
import { FicheExpressionBesoinDialogComponent } from './fiche-expression-besoin-dialog/fiche-expression-besoin-dialog.component';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { ApplicationSettingsService } from '../application-settings/application-settings.service';
import * as fileSaver from 'file-saver';
import { FebViewerComponent } from './feb-viewer/feb-viewer.component';
import { UserProfile } from 'src/app/shared/models/user-profile';
import { forkJoin } from 'rxjs';
import { LiveAnnouncer } from '@angular/cdk/a11y';

import { ChangeDetectorRef } from '@angular/core';
import { DuplicateFebDialogComponent } from 'src/app/shared/dialogs/duplicate-feb-dialog/duplicate-feb-dialog.component';
import { FebPdfService } from './feb-pdf.service';

@Component({
  selector: 'app-fiche-expression-besoin',
  templateUrl: './fiche-expression-besoin.component.html',
  styleUrls: ['./fiche-expression-besoin.component.less']
})
export class FicheExpressionBesoinComponent implements AfterViewInit, OnInit {

  fiches: MatTableDataSource<IFicheExpressionBesoinResponse>;
  displayedColumns: string[] = [
    'febLabel', 'febDemandeurNom', 'febProjectNom',
    'febLocalisationLabel', 'createdAt', 'febStatut', 'actions'
  ];
  readonly FebStatut = FebStatut;
  loading = false;

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private febService: FicheExpressionBesoinService,
    public authenticationService: AuthenticationService,
    private currentUserService: CurrentUserService,
    private cdRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private febPdfService: FebPdfService,
    public theme: ThemeService,
    private snackbarService: SnackbarService,
    private applicationSettingsService: ApplicationSettingsService,
    private _liveAnnouncer: LiveAnnouncer
  ) {
    this.fiches = new MatTableDataSource();
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadFiches();
    console.log("current user : " , this.currentUserService.getCurrentUser());
  }

  ngAfterViewInit(): void {
    this.fiches.sort = this.sort;

    this.fiches.sortingDataAccessor = (item, property) => {
      if (property === 'createdAt') {
        return new Date(item.createdAt).getTime();
      }
      return (item as any)[property];
    };

    this.sort.active = 'createdAt';
    this.sort.direction = 'desc';

    this.cdRef.detectChanges();
  }

  announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(
        `Tri ${sortState.direction === 'asc' ? 'ascendant' : 'descendant'}`
      );
    } else {
      this._liveAnnouncer.announce('Tri réinitialisé');
    }
  }

  // ── Chargement ────────────────────────────────────────────────────────────

 loadFiches(): void {
  const user = this.currentUserService.getCurrentUser();
  const apps = this.currentUserService.getUserApplications();
  const profile = user?.role ?? '';

  this.loading = true;

  let febs$;

  if (this.authenticationService.isUserAdminOrServiceManagerOrManagerCDS()) {
    febs$ = this.febService.getAll();
  }
  else if (profile === UserProfile.CP_CDS) {
    // CAS ACTEUR CDC → status != Crée
    febs$ = this.febService.getByApplicationsAndStatusNotEqual(apps ,FebStatut.CREEE);
  }
  else {
    febs$ = this.febService.getByApplications(apps);
  }

  forkJoin({ febs: febs$ }).subscribe({
    next: ({ febs }) => {
      this.fiches.data = febs;
      this.loading = false;
    },
    error: (err) => {
      this.snackbarService.openSnackBar(
        this.extractErrorMessage(err, 'Erreur lors du chargement des FEB'),
        'error-snack-bar'
      );
      this.loading = false;
    }
  });
}

  // ── Filtre ────────────────────────────────────────────────────────────────

  applyFilter(event: Event): void {
    this.fiches.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  // ── Dialogs ───────────────────────────────────────────────────────────────

  openDialog(fiche: IFicheExpressionBesoinResponse | null = null): void {
    const dialogRef = this.dialog.open(FicheExpressionBesoinDialogComponent, {
      width: '900px', maxWidth: '98vw', maxHeight: '90vh', data: fiche
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadFiches();
    });
  }

  openViewer(row: IFicheExpressionBesoinResponse): void {
    this.dialog.open(FebViewerComponent, {
      data: row,
      width: '860px',
      maxWidth: '95vw',
      height: '85vh',
      panelClass: 'feb-viewer-panel',
      autoFocus: false
    });
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  envoyerAuCds(fiche: IFicheExpressionBesoinResponse): void {
    this.dialogService
      .openConfirmDialog('"' + fiche.febLabel + '"', 'Envoyer au fournisseur ?')
      .afterClosed()
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.febService.envoyerAuCds(fiche.febId).subscribe({
          next: () => {
            this.snackbarService.openSnackBar('Fiche envoyée au fournisseur avec succès');
            this.loadFiches();
          },
          error: (err) => {
            this.snackbarService.openSnackBar(
              this.extractErrorMessage(err, 'Erreur lors de l\'envoi au fournisseur'),
              'error-snack-bar'
            );
          }
        });
      });
  }

 duplicateFiche(fiche: IFicheExpressionBesoinResponse): void {
  const confirmRef = this.dialog.open(
    DuplicateFebDialogComponent,
    {
      width: '500px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      data: {
        febLabel: fiche.febLabel,
        febNumber: fiche.febNumber
      }
    }
  );

  confirmRef.afterClosed().subscribe((confirmed: boolean) => {
    if (!confirmed) {
      return;
    }

    this.loading = true;

    this.febService.duplicate(fiche.febId).subscribe({
      next: duplicatedFeb => {
        this.loading = false;

        this.snackbarService.openSnackBar(
          'FEB dupliquée avec succès. Veuillez renseigner le label et les dates.'
        );

        const editDialogRef = this.dialog.open(
          FicheExpressionBesoinDialogComponent,
          {
            width: '900px',
            maxWidth: '98vw',
            maxHeight: '90vh',
            data: duplicatedFeb
          }
        );

        editDialogRef.afterClosed().subscribe(() => {
          // Mise à jour de la table, que la modification soit validée ou annulée
          this.loadFiches();
        });
      },
      error: err => {
        this.loading = false;

        this.snackbarService.openSnackBar(
          this.extractErrorMessage(
            err,
            'Erreur lors de la duplication de la FEB'
          ),
          'error-snack-bar'
        );
      }
    });
  });
}
isFebComplete(fiche: IFicheExpressionBesoinResponse): boolean {
  const hasLabel =
    typeof fiche.febLabel === 'string' &&
    fiche.febLabel.trim().length > 0;

  const hasDateDemande = !!fiche.febDateDemande;
  const hasDateDemarrage = !!fiche.febDateDemarrage;
  const hasDateFinPrevisionnelle =
    !!fiche.febDateFinPrevisionnelle;

  return (
    hasLabel &&
    hasDateDemande &&
    hasDateDemarrage &&
    hasDateFinPrevisionnelle
  );
}

  deleteFiche(fiche: IFicheExpressionBesoinResponse): void {
    if (!this.canDelete(fiche)) return;
    const confirmation = 'Supprimer la fiche "' + fiche.febLabel + '" ?'
      + (fiche.devisId || fiche.devisStatut ? ' Le devis associé sera également supprimé.' : '');
    this.dialogService
      .openConfirmDialog(confirmation)
      .afterClosed()
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.febService.delete(fiche.febId).subscribe({
          next: () => {
            this.snackbarService.openSnackBar('Fiche supprimée avec succès');
            this.fiches.data = this.fiches.data.filter(f => f.febId !== fiche.febId);
          },
          error: (err) => {
            this.snackbarService.openSnackBar(
              this.extractErrorMessage(err, 'Erreur lors de la suppression'),
              'error-snack-bar'
            );
          }
        });
      });
  }

  async downloadFeb(fiche: IFicheExpressionBesoinResponse): Promise<void> {
    this.snackbarService.openSnackBar('Generation de la fiche en cours...');

    try {
      const fullFeb = await this.fetchFebById(fiche.febId);
      await this.febPdfService.download(fullFeb);
    } catch (err) {
      this.snackbarService.openSnackBar(
        this.extractErrorMessage(err, 'Erreur lors de la generation du document FEB'),
        'error-snack-bar'
      );
    }
  }

  canDownloadFeb(fiche: IFicheExpressionBesoinResponse): boolean {
    return !!fiche?.febId;
  }

  exportData(): void {
    const febIds = this.fiches.data
      .map(fiche => fiche?.febId)
      .filter((id): id is string => !!id);

    if (!febIds.length) {
      this.snackbarService.openSnackBar('Aucune FEB à exporter', 'error-snack-bar');
      return;
    }

    this.febService.exportFebData(febIds).subscribe({
      next: (data) => {
        if (!data) {
          this.snackbarService.openSnackBar('Aucune FEB à exporter', 'error-snack-bar');
          return;
        }
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        fileSaver.saveAs(
          blob,
          'FEB - ' +
          this.applicationSettingsService.applicationSettings.CDS_name +
          ' - ' +
          new Date().toLocaleDateString().replace('_', '-') +
          ' ' +
          new Date().toLocaleTimeString().replace('_', '-')
        );
        this.snackbarService.openSnackBar('Les FEB ont été exportées avec succès');
      },
      error: (err) => {
        this.snackbarService.openSnackBar(
          this.extractErrorMessage(err, 'Erreur lors de l\'export des FEB'),
          'error-snack-bar'
        );
      }
    });
  }

  // ── Droits ────────────────────────────────────────────────────────────────

  canCreate(): boolean {
    const user = this.currentUserService.getCurrentUser();
    return !!user && ![
      UserProfile.CP_CDS, UserProfile.MANAGER_CDS,
      UserProfile.GUEST_CDS, UserProfile.GUEST_SNCF
    ].includes(user.role as UserProfile);
  }

  canEdit(fiche: IFicheExpressionBesoinResponse): boolean {
    return this.authenticationService.isUserAdmin() || fiche.febStatut === FebStatut.CREEE;
  }

  canDelete(fiche: IFicheExpressionBesoinResponse): boolean {
    if (fiche.febStatut === FebStatut.VALIDEE
      || fiche.febDemandId
      || fiche.devisStatut === DevisStatut.ACCEPTE
      || fiche.devisDemandId) {
      return false;
    }
    if (this.authenticationService.isUserAdmin()) return true;
    return fiche.febStatut === FebStatut.CREEE && !fiche.devisId && !fiche.devisStatut;
  }

 canEnvoyer(fiche: IFicheExpressionBesoinResponse): boolean {
  const profile = this.authenticationService.getUser()?.profile;

  const hasPermission =
    this.authenticationService.isUserAdmin()
    || profile === UserProfile.CP_SNCF
    || profile === UserProfile.SERVICE_MANAGER;

  return (
    fiche.febStatut === FebStatut.CREEE
    && hasPermission
    && this.isFebComplete(fiche)
  );
}

canDuplicate(fiche: IFicheExpressionBesoinResponse): boolean {
  const user = this.currentUserService.getCurrentUser();

  if (!user) {
    return false;
  }

  return ![
    UserProfile.CP_CDS,
    UserProfile.MANAGER_CDS,
    UserProfile.GUEST_CDS,
    UserProfile.GUEST_SNCF
  ].includes(user.role as UserProfile);
}

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }

  private fetchFebById(febId: string): Promise<IFicheExpressionBesoinResponse> {
    return new Promise((resolve, reject) => {
      this.febService.getById(febId).subscribe({
        next: (feb) => resolve(feb),
        error: (err) => reject(err),
      });
    });
  }
}
